"use client"

// import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { type SubmitHandler, useFormContext } from "react-hook-form"

import { Button } from "@workspace/web-ui/components/button"
import { cn } from "@workspace/web-ui/lib/utils"

import { useConsultationFormStore } from "../store/use-consultation-form-store"
import { MedicationSelectionStep } from "./medication-selection-step"
import { ConsultationTypeStep } from "./consultation-type-step"
import { consultationStepsData } from "../data/data"
import type { ConsultationFormFieldName, ConsultationFormSchema } from "../schema/schema"
import { useConsultationIntegration } from "../hooks/use-consultation-integration"
import { useMedicalPlanMutation } from "~/features/public/api/mutations"
import { Loader2 } from "lucide-react"
import { useMedicalPlanStore } from "../store/use-medical-plan-variable-store"
// import { useEncounterIdStore } from "../../welcome/store/use-encounter-id-store"
// import { useAuth } from "~/providers/auth.provider"
// import { useConsultationIntegration } from "~/hooks/use-consultation-integration"

interface FormHandlerProps {
  submit: boolean
  activeStep: number
  nextStep: (step?: number) => void
  previousStep: (step?: number) => void
}

export const ConsultationForm = ({ 
  activeStep, 
  nextStep, 
  previousStep,
  // submit
 }: FormHandlerProps) => {
  const { trigger, handleSubmit, reset } = useFormContext<ConsultationFormSchema>()
  const { updateFields, clearFields } = useConsultationFormStore()
  const { mutate: createMedicalPlan, isPending: creatingPlan } = useMedicalPlanMutation();
  const { 
    createFromFormData, 
  } = useMedicalPlanStore();
  // const { encounterId } = useEncounterIdStore()
  // const {user} = useAuth()

  // Use the integration hook
  useConsultationIntegration()

  const navigate = useNavigate()

//   const isLastStep = activeStep === 2

  const submitForm: SubmitHandler<ConsultationFormSchema> = (formData) => {
    console.log("ConsultationForm data being submitted:", formData);
    const successUrl = `${window.location.origin}/consultations/confirmation`;
    const cancelUrl = `${window.location.origin}/consultations/error`;

    const medicalPlanVariables = {
      program: "weight_loss",
      session_id: null, // This needs to be dynamically set if available, otherwise it's an empty string for now
      medication: {
        name: formData.medication.name,
        cost: formData.medication.cost,
      },
      consultation: {
        name: formData.consultationType.name,
        cost: formData.consultationType.cost,
      },
      appointment: {
        title: "Initial Consultation",
        description: "Medical consultation and plan setup",
        location: "Online",
        date: new Date().toISOString().split('T')[0]!,
        time: new Date().toTimeString().split(' ')[0]!,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        program: "weight_loss",
        session_id: null, // This needs to be dynamically set if available, otherwise it's an empty string for now
        additionalProp1: {},
      },
      success_url: successUrl,
      cancel_url: cancelUrl,
    };

    createFromFormData(formData, successUrl, cancelUrl);

    if(formData.consultationType.name === "live") {
      navigate({to: "/live-consultation"})
    } else {
      createMedicalPlan(medicalPlanVariables, {
        onSuccess: (response) => {
          if (response.payment?.url) {
            window.location.replace(response.payment.url);
          } else {
            navigate({ to: "/consultations" });
          }
          reset();
          clearFields();
        },
        onError: (error) => {
          console.error("Medical plan submission failed:", error);
          // Optionally handle error, e.g., show a toast message
        },
      });
    }
  }

  const handleNext = async () => {
    const fields = consultationStepsData[activeStep - 1]?.form?.map((fm) => fm.name)
    const output = await trigger(fields as ConsultationFormFieldName[], {
      shouldFocus: true,
    })
    if (!output) return

    if (activeStep === 1) {
      nextStep()
    } else if (activeStep === 2) {
      handleSubmit(submitForm)()
    }
  }

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      {activeStep === 1 && <MedicationSelectionStep data={consultationStepsData[0]!} updateFields={updateFields} />}

      {activeStep === 2 && <ConsultationTypeStep data={consultationStepsData[1]!} updateFields={updateFields} />}

      <div className="fixed bottom-0 left-0 right-0 z-10 border border-t border-t-[#0000001A] bg-white shadow-[0px_-2px_10px_0px_#00000014]">
        <div className="mx-auto flex w-full items-center justify-between px-4 py-3 sm:max-w-[524px] sm:px-6 sm:py-4 lg:max-w-[624px] lg:px-8 lg:py-2">
          {activeStep !== 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => previousStep()}
              className="h-[40px] w-[120px] cursor-pointer rounded-2xl border-[1px] border-solid border-[#DCDCDE] bg-transparent px-3 py-2 text-sm text-[#7F8493] shadow-[0px_1px_2px_0px_#0000001A] sm:h-[48px] sm:w-[120px] sm:px-4 sm:text-base lg:h-[57px] lg:w-[144px] lg:px-6 lg:py-3 lg:text-lg"
            >
              Back
            </Button>
          )}
          <Button
            type="button"
            onClick={handleNext}
            disabled={creatingPlan}
            className={cn(
              "h-[40px] cursor-pointer ml-2 w-full rounded-2xl bg-[#4272DD] px-3 py-2 text-sm text-white shadow-[0px_1px_2px_0px_#0000001A] sm:h-[48px] sm:px-4 sm:text-base lg:h-[57px] lg:px-8 lg:py-4 lg:text-lg",
              activeStep === 2 && "",
              creatingPlan && "opacity-70 cursor-not-allowed"
            )}
          >
            {creatingPlan ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Creating Plan...</span>
              </div>
            ) : (
              activeStep === 2 ? "Confirm" : "Continue"
            )}
          </Button>
        </div>
      </div>
    </form>
  )
}
